import { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useCurrentUser } from './hooks/useUsers';

// Layouts
import { DashboardLayout } from './layouts/DashboardLayout';
import { AuthLayout } from './layouts/AuthLayout';

// Pages
import { LoginPage } from './pages/auth/LoginPage';
import { CallbackPage } from './pages/auth/CallbackPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { DepartmentsPage } from './pages/settings/DepartmentsPage';
import { ShiftsPage } from './pages/settings/ShiftsPage';
import { PositionsPage } from './pages/settings/PositionsPage';
import { StationsPage } from './pages/settings/StationsPage';
import { UsersPage } from './pages/settings/UsersPage';
import { TeamPage } from './pages/settings/TeamPage';
import { CamerasPage } from './pages/cameras/CamerasPage';
import { VideosPage } from './pages/videos/VideosPage';
import { AcceptInvitationPage } from './pages/invite/AcceptInvitationPage';
import { OperatorsPage } from './pages/operators/OperatorsPage';
import { OperatorDetailPage } from './pages/operators/OperatorDetailPage';
import { SOPsPage } from './pages/sops/SOPsPage';
import { SOPDetailPage } from './pages/sops/SOPDetailPage';
import ProposalsInboxPage from './pages/sops/ProposalsInboxPage';
import ProposalDetailPage from './pages/sops/ProposalDetailPage';
import { AssessmentsPage } from './pages/assessments/AssessmentsPage';
import { NewAssessmentPage } from './pages/assessments/NewAssessmentPage';
import { NewAssessmentV2Page } from './pages/assessments/NewAssessmentV2Page';
import { AssessmentDetailPage } from './pages/assessments/AssessmentDetailPage';
import PendingReviewPage from './pages/assessments/PendingReviewPage';
import SectionReviewPage from './pages/assessments/SectionReviewPage';
import { TriggerDetailPage } from './pages/assessments/TriggerDetailPage';
import { SkillMatrixPage } from './pages/skill-matrix/SkillMatrixPage';
import { LearningPage } from './pages/learning/LearningPage';
import { GeneratedTrainingPage } from './pages/learning/GeneratedTrainingPage';
import { AnalyticsPage } from './pages/analytics/AnalyticsPage';
// Legacy KPI pages -- kept for reference but routes now redirect to /analytics/kpi
// import { ResponseTimePage } from './pages/analytics/ResponseTimePage';
// import { ActivityCountPage } from './pages/analytics/ActivityCountPage';
// import { ChecklistCompliancePage } from './pages/analytics/ChecklistCompliancePage';
import { OperatorFeedbackPage } from './pages/analytics/OperatorFeedbackPage';
import { CoachEffectivenessPage } from './pages/analytics/CoachEffectivenessPage';
import { KpiAnalyticsPage } from './pages/analytics/KpiAnalyticsPage';
import { ShiftComparisonPage } from './pages/analytics/ShiftComparisonPage';
import { ShiftDrillDownPage } from './pages/analytics/ShiftDrillDownPage';
import { Ring2EffectivenessPage } from './pages/analytics/Ring2EffectivenessPage';
import { LivingSopsAnalyticsPage } from './pages/analytics/LivingSopsAnalyticsPage';
import { NotFoundPage } from './pages/NotFoundPage';
// Phase 30 Wave 1 (30.1.2) — locked tabs (Command Center + Machine Fleet)
import { CommandCenterPage } from './pages/CommandCenterPage';
import { MachineFleetPage } from './pages/MachineFleetPage';
import { ZonesPage } from './pages/settings/ZonesPage';
import { ChecklistsPage } from './pages/settings/ChecklistsPage';
import { KpiLibraryPage } from './pages/kpis/KpiLibraryPage';
import { AssessmentSchedulesPage } from './pages/settings/AssessmentSchedulesPage';
// Phase 30 Wave 4 (30.4.2) — Settings → Platform sub-tabs (super_admin only)
import { CameraRegistryPage } from './pages/settings/platform/CameraRegistryPage';
import { OrganizationsPage } from './pages/settings/platform/OrganizationsPage';

// Operator Portal Pages
import { OperatorLayout } from './pages/operator/OperatorLayout';
import { GoalsTab } from './pages/operator/GoalsTab';
import { ProgressTab } from './pages/operator/ProgressTab';
import { ContentTab } from './pages/operator/ContentTab';
import { TrainingViewerPage } from './pages/operator/TrainingViewerPage';
import { SopViewerPage } from './pages/operator/SopViewerPage';
import { QuizPlayerPage } from './pages/operator/QuizPlayerPage';
import { SequencerPlayerPage } from './pages/operator/SequencerPlayerPage';
import { CoachPage } from './pages/operator/CoachPage';

// Components
import { LoadingSpinner } from './components/ui/LoadingSpinner';

/**
 * Protected route wrapper
 * Redirects to login if not authenticated, passing the requested path and
 * query as `state.returnTo` so sign-in can return to it (G-12, lib/return-to.ts).
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth0();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

/**
 * Role-based redirect component
 * Redirects operator-role users to the Operator Portal.
 *
 * Phase 19.S frontend migration (May 6, 2026) — role now flows from the
 * DB-canonical /api/me response, not the JWT claim. This means a role
 * change (operator → admin or vice versa) propagates without a forced
 * re-login. While /api/me is loading we render a spinner instead of the
 * admin shell, so operators never briefly see admin UI on first paint.
 */
function OperatorRedirect({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: auth0Loading } = useAuth0();
  const { data: currentUser, isLoading: meLoading } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();

  const role = currentUser?.role;

  useEffect(() => {
    if (!auth0Loading && isAuthenticated && !meLoading && currentUser) {
      // If operator trying to access admin routes, redirect to operator portal
      if (role === 'operator' && !location.pathname.startsWith('/operator')) {
        navigate('/operator', { replace: true });
      }

      // Redirect unknown/removed roles (e.g. viewer) to login
      if (role && !['super_admin', 'admin', 'trainer', 'manager', 'operator'].includes(role)) {
        navigate('/login', { replace: true });
      }
    }
  }, [auth0Loading, isAuthenticated, meLoading, currentUser, role, location.pathname, navigate]);

  // While /api/me is in flight, don't render admin content. This prevents
  // operators from briefly seeing the admin shell on first paint.
  if (isAuthenticated && meLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Don't render admin content for operators
  if (isAuthenticated && role === 'operator') {
    return null; // Will redirect via useEffect
  }

  return <>{children}</>;
}

/**
 * Legacy redirect for /operator/training/:assignmentId bookmarks.
 * React Router v7 <Navigate> does not support dynamic segment substitution,
 * so we use a component that reads the param and redirects.
 */
function TrainingLegacyRedirect() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  return <Navigate to={`/operator/content/${assignmentId}`} replace />;
}

/**
 * Main App component with routing
 */
export default function App() {
  return (
    <Routes>
      {/* Auth routes (public) */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route path="/invite/:token" element={<AcceptInvitationPage />} />
      </Route>

      {/* Dashboard routes (protected, redirects operators to /operator) */}
      <Route
        element={
          <ProtectedRoute>
            <OperatorRedirect>
              <DashboardLayout />
            </OperatorRedirect>
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Locked "Command Center" placeholder — sub-tab under Dashboard */}
        <Route path="/dashboard/command-center" element={<CommandCenterPage />} />

        {/* Operators routes */}
        <Route path="/operators" element={<OperatorsPage />} />
        <Route path="/operators/:id" element={<OperatorDetailPage />} />

        {/* SOPs routes */}
        <Route path="/sops" element={<SOPsPage />} />
        {/* Phase 23.2.1: proposals inbox — MUST be before `/sops/:id` so React
            Router matches the specific segment first. */}
        <Route path="/sops/proposals" element={<ProposalsInboxPage />} />
        {/* Phase 23.2.2: proposal detail — also before `/sops/:id`. Router
            matches by specificity but we keep explicit ordering for clarity. */}
        <Route path="/sops/proposals/:id" element={<ProposalDetailPage />} />
        {/* Locked "Machine Fleet" placeholder — under Library */}
        <Route path="/sops/machine-fleet" element={<MachineFleetPage />} />
        <Route path="/sops/:id" element={<SOPDetailPage />} />

        {/* Assessment routes */}
        <Route path="/assessments" element={<AssessmentsPage />} />
        <Route path="/assessments/new" element={<NewAssessmentPage />} />
        <Route path="/assessments/new-v2" element={<NewAssessmentV2Page />} />
        <Route path="/assessments/pending-review" element={<PendingReviewPage />} />
        <Route path="/assessments/section-review" element={<SectionReviewPage />} />
        <Route path="/assessments/triggers/:id" element={<TriggerDetailPage />} />
        <Route path="/assessments/:id" element={<AssessmentDetailPage />} />

        {/* Skill Matrix */}
        <Route path="/skill-matrix" element={<SkillMatrixPage />} />

        {/* Analytics */}
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/analytics/shift-comparison" element={<ShiftComparisonPage />} />
        <Route path="/analytics/shift-comparison/:shiftId" element={<ShiftDrillDownPage />} />
        <Route path="/analytics/kpi" element={<KpiAnalyticsPage />} />
        {/* Legacy KPI analytics routes -- redirect to unified page */}
        <Route path="/analytics/response-time" element={<Navigate to="/analytics/kpi?code=response_time" replace />} />
        <Route path="/analytics/activity-count" element={<Navigate to="/analytics/kpi?code=activity_count" replace />} />
        {/* checklist_verify was deleted in Phase 24 hygiene (migration 0023, PR #85) — redirect bare so the user lands on the KPI picker without an invalid pre-selection. */}
        <Route path="/analytics/checklist-compliance" element={<Navigate to="/analytics/kpi" replace />} />
        <Route path="/analytics/operator-feedback" element={<OperatorFeedbackPage />} />
        <Route path="/analytics/coach-effectiveness" element={<CoachEffectivenessPage />} />
        <Route path="/analytics/ring-2-effectiveness" element={<Ring2EffectivenessPage />} />
        <Route path="/analytics/living-sops" element={<LivingSopsAnalyticsPage />} />

        {/* KPI Library */}
        <Route path="/kpis" element={<KpiLibraryPage />} />

        {/* Learning routes */}
        <Route path="/learning" element={<LearningPage />} />
        <Route path="/learning/generated" element={<GeneratedTrainingPage />} />

        {/* Settings routes */}
        <Route path="/settings/departments" element={<DepartmentsPage />} />
        <Route path="/settings/shifts" element={<ShiftsPage />} />
        <Route path="/settings/positions" element={<PositionsPage />} />
        <Route path="/settings/stations" element={<StationsPage />} />
        <Route path="/settings/zones" element={<ZonesPage />} />
        <Route path="/settings/checklists" element={<ChecklistsPage />} />
        <Route path="/settings/users" element={<UsersPage />} />
        <Route path="/settings/team" element={<TeamPage />} />
        <Route path="/settings/cameras" element={<CamerasPage />} />
        <Route path="/settings/video-logs" element={<VideosPage />} />
        <Route path="/settings/kpi" element={<Navigate to="/kpis" replace />} />
        <Route path="/settings/schedules" element={<AssessmentSchedulesPage />} />

        {/* Phase 30 Wave 4 (30.4.2 + 30.4.3b) — Settings → Platform sub-tabs.
            Super_admin-only; both pages internally redirect to /dashboard
            for level < 6 (defense-in-depth alongside the requireSuperAdmin
            backend guard and the level-6 nav filter). */}
        <Route path="/settings/platform/camera-registry" element={<CameraRegistryPage />} />
        <Route path="/settings/platform/organizations" element={<OrganizationsPage />} />
      </Route>

      {/* Operator Portal routes (protected, separate layout) */}
      <Route
        element={
          <ProtectedRoute>
            <OperatorLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/operator" element={<Navigate to="/operator/content" replace />} />
        <Route path="/operator/goals" element={<GoalsTab />} />
        <Route path="/operator/progress" element={<ProgressTab />} />
        <Route path="/operator/content" element={<ContentTab />} />
        <Route path="/operator/sops/:sopId" element={<SopViewerPage />} />
        <Route path="/operator/content/:assignmentId" element={<TrainingViewerPage />} />
        {/* Legacy redirects for old /operator/training bookmarks */}
        <Route path="/operator/training" element={<Navigate to="/operator/content" replace />} />
        <Route path="/operator/training/:assignmentId" element={<TrainingLegacyRedirect />} />
      </Route>

      {/* Full-screen operator player pages (no tab bar — outside OperatorLayout) */}
      <Route
        path="/operator/practice/quiz/:sectionId"
        element={
          <ProtectedRoute>
            <QuizPlayerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operator/practice/sequencer/:taskId"
        element={
          <ProtectedRoute>
            <SequencerPlayerPage />
          </ProtectedRoute>
        }
      />

      {/* Full-screen AI Coach (no tab bar — outside OperatorLayout) */}
      <Route
        path="/operator/coach"
        element={
          <ProtectedRoute>
            <CoachPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operator/coach/:sessionId"
        element={
          <ProtectedRoute>
            <CoachPage />
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
